import { Moment } from 'moment';
import { FilterItemOperationEnum, SortDirectionEnum } from '../../core/models/enum';
import { capitalizeFirstLetter } from '../../helpers/TransformHelper';
import {
    EventLightVm,
    FilterItemOfDateTime,
    GetMatchAnalysisQuery,
    MatchAnalysisPlayerVm,
    MatchAnalysisVm,
    MatchDetailVm,
    MatchesClient,
    MatchFilter,
    MatchVm,
    PaginatedListOfMatchVm,
    PaginationFilterOfMatchFilter,
} from '../../utils/api';

/** Every Insights match query is club scoped; the rest narrows it down. */
export interface ClubMatchQuery {
    clubId: number;
    teamId?: number;
    opponentTeamId?: number;
    competitionId?: number;
    dateRange?: [Moment, Moment] | null;
    pageIndex?: number;
    pageSize: number;
}

export const buildStartDateFilter = (
    dateRange?: [Moment, Moment] | null
): FilterItemOfDateTime[] | undefined => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return undefined;
    return [
        new FilterItemOfDateTime({
            value: dateRange[0].clone().startOf('day').utc().toDate(),
            operation: FilterItemOperationEnum.GreaterThanEqual,
        }),
        new FilterItemOfDateTime({
            value: dateRange[1].clone().endOf('day').utc().toDate(),
            operation: FilterItemOperationEnum.LessThanEqual,
        }),
    ];
};

/** Runs promises a few at a time so a season page cannot flood the API in one burst. */
export const mapWithConcurrency = async <TIn, TOut>(
    items: TIn[],
    limit: number,
    worker: (item: TIn) => Promise<TOut>
): Promise<TOut[]> => {
    const results: TOut[] = new Array(items.length);
    let cursor = 0;

    const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
        for (;;) {
            const index = cursor;
            cursor += 1;
            if (index >= items.length) return;
            results[index] = await worker(items[index]);
        }
    });

    await Promise.all(runners);
    return results;
};

export interface MatchBundle {
    detail: MatchDetailVm;
    events: EventLightVm[];
}

/**
 * The batch endpoint returns a lean analysis VM; the rest of Insights (and its tests) works
 * against MatchDetailVm, so reshape once here instead of touching every aggregation.
 */
const toBundle = (analysis: MatchAnalysisVm): MatchBundle => ({
    detail: MatchDetailVm.fromJS({
        id: analysis.id,
        homeTeamId: analysis.homeTeamId,
        homeTeamDisplayName: analysis.homeTeamDisplayName,
        awayTeamId: analysis.awayTeamId,
        awayTeamDisplayName: analysis.awayTeamDisplayName,
        competitionId: analysis.competitionId,
        competitionDisplayName: analysis.competitionDisplayName,
        startDate: analysis.startDate,
        statusId: analysis.statusId,
        homeTeamGoalsTotal: analysis.homeTeamGoalsTotal,
        awayTeamGoalsTotal: analysis.awayTeamGoalsTotal,
        players: (analysis.players || []).map((player: MatchAnalysisPlayerVm) => ({
            id: player.id,
            playerId: player.playerId,
            teamId: player.teamId,
            capNumber: player.capNumber,
            player: { id: player.playerId, name: player.name, surname: player.surname },
        })),
    }),
    events: analysis.events || [],
});

/*
 * ponytail: bundles are memoised for the life of the page so switching sample size, opening a
 * player and coming back, or moving between Insights pages re-uses what is already loaded.
 * Ceiling: a match edited elsewhere stays stale until reload, which is fine for a read-only
 * analysis surface. Upgrade path is invalidating on the match events hub.
 */
const bundleCache = new Map<number, MatchBundle>();

export const clearMatchBundleCache = (): void => bundleCache.clear();

/**
 * Lineups plus light events for a list of matches, in one request for whatever is not cached.
 */
export const loadMatchBundles = async (matches: MatchVm[]): Promise<MatchBundle[]> => {
    const wanted = matches.map((match: MatchVm) => match.id);
    const missing = wanted.filter((id: number) => !bundleCache.has(id));

    if (missing.length > 0) {
        const analysed = await new MatchesClient().getAnalysis(
            new GetMatchAnalysisQuery({ matchIds: missing })
        );
        (analysed || []).forEach((analysis: MatchAnalysisVm) =>
            bundleCache.set(analysis.id, toBundle(analysis))
        );
    }

    // Matches the API refused (or does not know) simply drop out of the sample.
    return wanted
        .map((id: number) => bundleCache.get(id))
        .filter((bundle): bundle is MatchBundle => bundle !== undefined);
};

/** Newest first, matching how coaches read a season. */
export const fetchClubMatches = (query: ClubMatchQuery): Promise<PaginatedListOfMatchVm> => {
    const filter = new MatchFilter();
    filter.involvingClubId = query.clubId;
    filter.involvingTeamId = query.teamId;
    filter.involvingTeamId2 = query.opponentTeamId;
    filter.competitionId = query.competitionId;
    filter.startDate = buildStartDateFilter(query.dateRange);

    return new MatchesClient().getPaginated(
        new PaginationFilterOfMatchFilter({
            filter,
            pageIndex: query.pageIndex || 1,
            pageSize: query.pageSize,
            sortBy: capitalizeFirstLetter('startDate'),
            sortOrder: SortDirectionEnum.DESC,
        })
    );
};
